/**
 * Parent → Daily Insights
 *
 * Lists every approved daily report for the parent's children, structured
 * identically to the observer's "My Reports" page so families have a clear
 * timeline of what was reviewed by the principal and now visible to them.
 *
 * Card structure mirrors the observer report card (mood / engagement / growth
 * pills, AI · CALL chip, principal's note callout, "View Full Report" button).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, RefreshCw, CheckCircle, Calendar, Baby, ArrowLeft, Sparkles, MessageSquare, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import ParentNav from '../components/ParentNav';
import InsightCard from '../components/InsightCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helpers at module scope (ESLint immutability friendly)
const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const moodClass = (mood) => {
  const m = (mood || '').toLowerCase();
  if (m === 'happy' || m === 'excited') return 'bg-green-100 text-green-700';
  if (m === 'calm') return 'bg-blue-100 text-blue-700';
  if (m === 'curious' || m === 'engaged') return 'bg-violet-100 text-violet-700';
  if (m === 'upset' || m === 'anxious') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-700';
};

const engagementClass = (score) => {
  if (score >= 60) return 'bg-green-100 text-green-700';
  if (score >= 40) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const growthMeta = (rating) => {
  const key = (rating || '').toLowerCase();
  if (key === 'balanced' || key === 'excellent' || key === 'good') {
    return { label: 'Balanced Growth', cls: 'bg-blue-100 text-blue-700' };
  }
  if (key === 'moderate' || key === 'fair') {
    return { label: 'Moderate Growth', cls: 'bg-amber-100 text-amber-700' };
  }
  if (key === 'limited' || key === 'needs_work') {
    return { label: 'Limited Growth', cls: 'bg-red-100 text-red-700' };
  }
  return { label: rating || '—', cls: 'bg-gray-100 text-gray-700' };
};

const ParentDailyInsight = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('all');
  const [insightCardReport, setInsightCardReport] = useState(null);

  const loadReports = useCallback(async (childId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('parent_token');
      if (!token) {
        navigate('/parent/login');
        return;
      }
      const url = childId && childId !== 'all'
        ? `${BACKEND_URL}/api/parent/approved-reports?child_id=${childId}`
        : `${BACKEND_URL}/api/parent/approved-reports`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(data.reports || []);
    } catch (e) {
      toast({
        title: 'Could not load reports',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  const loadChildren = useCallback(async () => {
    try {
      const token = localStorage.getItem('parent_token');
      if (!token) return;
      const { data } = await axios.get(`${BACKEND_URL}/api/parent/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChildren(data.children || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadChildren();
    loadReports('all');
  }, [loadChildren, loadReports]);

  const onSelectChild = (id) => {
    setSelectedChildId(id);
    loadReports(id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50" data-testid="parent-daily-insight">
      <ParentNav />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/parent/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                  Daily Insights
                </h1>
                <p className="text-gray-600 text-sm">Reports verified by the principal and shared with you</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => loadReports(selectedChildId)} data-testid="refresh-reports-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          {/* Child filter */}
          {children.length > 1 && (
            <div className="mb-6 flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={selectedChildId === 'all' ? 'default' : 'outline'}
                onClick={() => onSelectChild('all')}
                data-testid="child-filter-all"
              >
                All children
              </Button>
              {children.map((c) => (
                <Button
                  key={c.id}
                  size="sm"
                  variant={selectedChildId === c.id ? 'default' : 'outline'}
                  onClick={() => onSelectChild(c.id)}
                  data-testid={`child-filter-${c.id}`}
                  className="flex items-center gap-1.5"
                >
                  <Baby className="w-4 h-4" />
                  {c.name}
                </Button>
              ))}
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
            </div>
          )}

          {/* Empty state */}
          {!loading && reports.length === 0 && (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center" data-testid="parent-reports-empty">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-700 text-lg font-medium">No approved reports yet</p>
                <p className="text-gray-500 mt-1">
                  Reports will show up here as soon as the principal reviews them.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Reports list */}
          {!loading && reports.length > 0 && (
            <div className="space-y-4">
              {reports.map((r) => {
                const eng = r.engagement_score || {};
                const ogs = r.overall_growth_score || {};
                const growth = growthMeta(ogs.rating);
                const engText = eng.score !== undefined
                  ? `${eng.score}/100 · ${eng.label || ''}`
                  : '—';
                return (
                  <Card
                    key={r.id}
                    className="border-0 shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`parent-report-card-${r.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-full flex items-center justify-center shrink-0">
                            <FileText className="w-6 h-6 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold flex items-center gap-2 truncate">
                              {r.child_name}
                              {r.source && r.source.includes('call') && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                                  AI · CALL
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {fmtDateTime(r.approved_at || r.report_date)}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-green-100 text-green-700" data-testid="parent-report-status">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          approved
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm flex-wrap mb-3">
                        <span className={`px-2 py-1 rounded ${moodClass(r.child_mood)}`}>
                          Mood: {r.child_mood || 'neutral'}
                        </span>
                        <span className={`px-2 py-1 rounded ${engagementClass(eng.score || 0)}`}>
                          Engagement: {engText}
                        </span>
                        <span className={`px-2 py-1 rounded ${growth.cls}`}>
                          Growth Score: {growth.label}
                        </span>
                      </div>

                      {r.principal_feedback_for_parent && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
                            <MessageSquare className="w-3.5 h-3.5" /> Principal&apos;s note
                          </p>
                          <p className="text-sm text-amber-900 mt-1 italic">
                            &ldquo;{r.principal_feedback_for_parent}&rdquo;
                          </p>
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/parent/report/${r.id}`)}
                          data-testid={`view-parent-report-btn-${r.id}`}
                        >
                          View Full Report
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setInsightCardReport(r)}
                          className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white gap-1.5"
                          data-testid={`insight-card-btn-${r.id}`}
                        >
                          <ImageIcon className="w-3.5 h-3.5" /> Insight Card
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {insightCardReport && (
        <InsightCard
          report={insightCardReport}
          onClose={() => setInsightCardReport(null)}
        />
      )}
    </div>
  );
};

export default ParentDailyInsight;
